import React, { useEffect, useRef } from "react";
import { minimapStyles as styles } from './court.tailwind';
import CourtSvg1 from "./CourtSvg1";
import { DEFAULT_H, DEFAULT_W } from "./court.data";
const COURT_W = 1153;

const MinimapComponent = ({ viewBox, scale, onViewBoxChange, hideZoneName, isRotated }) => {
  const minimapRef = useRef(null);
  const minimapViewportRef = useRef(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });

  // In the updateMinimapViewport function in MinimapComponent
  const updateMinimapViewport = () => {
    if (!minimapRef?.current) return;

    const mW = minimapRef.current.clientWidth;
    const mH = minimapRef.current.clientHeight;

    if (isRotated) {
      const minimapScaleX = mW / DEFAULT_H;
      const minimapScaleY = mH / COURT_W;

      const boundedViewBox = {
        x: Math.max(0, Math.min(COURT_W - viewBox.w, viewBox.x)),
        y: Math.max(0, Math.min(DEFAULT_H - viewBox.h, viewBox.y)),
        w: viewBox.w,
        h: viewBox.h,
      };

      const minimapX = (DEFAULT_H - (boundedViewBox.y + boundedViewBox.h)) * minimapScaleX;
      const minimapY = boundedViewBox.x * minimapScaleY;
      const minimapW = boundedViewBox.h * minimapScaleX;
      const minimapH = boundedViewBox.w * minimapScaleY;

      if (minimapViewportRef.current) {
        minimapViewportRef.current.style.left = `${minimapX}px`;
        minimapViewportRef.current.style.top = `${minimapY}px`;
        minimapViewportRef.current.style.width = `${minimapW}px`;
        minimapViewportRef.current.style.height = `${minimapH}px`;
      }
    } else {
      const minimapScaleX = mW / COURT_W;
      const minimapScaleY = mH / DEFAULT_H;

      const boundedViewBox = {
        x: Math.max(0, Math.min(COURT_W - viewBox.w, viewBox.x)),
        y: Math.max(0, Math.min(DEFAULT_H - viewBox.h, viewBox.y)),
        w: viewBox.w,
        h: viewBox.h,
      };

      const minimapX = boundedViewBox.x * minimapScaleX;
      const minimapY = boundedViewBox.y * minimapScaleY;
      const minimapW = boundedViewBox.w * minimapScaleX;
      const minimapH = boundedViewBox.h * minimapScaleY;

      if (minimapViewportRef.current) {
        minimapViewportRef.current.style.left = `${minimapX}px`;
        minimapViewportRef.current.style.top = `${minimapY}px`;
        minimapViewportRef.current.style.width = `${minimapW}px`;
        minimapViewportRef.current.style.height = `${minimapH}px`;
      }
    }
  };

  const handleMinimapStart = (e) => {
    isDragging.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragStart.current = { x: clientX, y: clientY };
  };

  const handleMinimapMove = (e) => {
    if (!isDragging.current) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const minimapBounds = minimapRef.current.getBoundingClientRect();

    const dx = (clientX - dragStart.current.x) / minimapBounds.width;
    const dy = (clientY - dragStart.current.y) / minimapBounds.height;

    if (isRotated) {
      const newViewBox = {
        x: Math.max(0, Math.min(COURT_W - viewBox.w, viewBox.x + dy * COURT_W)),
        y: Math.max(0, Math.min(DEFAULT_H - viewBox.h, viewBox.y - dx * DEFAULT_H)),
        w: viewBox.w,
        h: viewBox.h,
      };
      onViewBoxChange(newViewBox);
    } else {
      const newViewBox = {
        x: Math.max(0, Math.min(COURT_W - viewBox.w, viewBox.x + dx * COURT_W)),
        y: Math.max(0, Math.min(DEFAULT_H - viewBox.h, viewBox.y + dy * DEFAULT_H)),
        w: viewBox.w,
        h: viewBox.h,
      };
      onViewBoxChange(newViewBox);
    }

    dragStart.current = { x: clientX, y: clientY };
  };

  const handleMinimapEnd = () => {
    isDragging.current = false;
  };
  
  useEffect(() => {
    const minimapViewport = minimapViewportRef.current;
    if (!minimapViewport) return;

    const handleMouseUp = () => handleMinimapEnd();

    minimapViewport.addEventListener("mousedown", handleMinimapStart);
    minimapViewport.addEventListener("touchstart", handleMinimapStart);
    window.addEventListener("mousemove", handleMinimapMove);
    window.addEventListener("touchmove", handleMinimapMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchend", handleMouseUp);

    return () => {
      minimapViewport.removeEventListener("mousedown", handleMinimapStart);
      minimapViewport.removeEventListener("touchstart", handleMinimapStart);
      window.removeEventListener("mousemove", handleMinimapMove);
      window.removeEventListener("touchmove", handleMinimapMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, [viewBox]);

  useEffect(() => {
    updateMinimapViewport();
  }, [viewBox, scale]);

  return (
    <div
      className={isRotated ? styles.rotatedMinimapContainer : styles.minimapContainer}
      ref={minimapRef}
    >
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        <svg
          width={isRotated ? "175.3%" : "100%"}
          height={isRotated ? "57%" : "100%"}
          xmlns="http://www.w3.org/2000/svg"
          viewBox={`0 0 ${COURT_W} ${DEFAULT_H}`}
          fill="none"
          style={isRotated ? {
            transform: 'rotate(90deg)',
            flexShrink: 0
          } : {}}
        >
          <CourtSvg1 hideZoneName={hideZoneName} hideHover={true} />
        </svg>
      </div>

      <div
        ref={minimapViewportRef}
        className={styles.minimapViewport}
        style={{
          position: "absolute",
          border: "2px solid #ff6347",
          backgroundColor: "rgba(255, 99, 71, 0.3)",
          // pointerEvents: "none", // Prevent interaction with the viewport
          transition: "all 0.2s ease",
        }}
      />
    </div>
  );
};

export default MinimapComponent;
